import React, {useState, useCallback, useContext} from 'react'
import {useDisclosure, Box} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {defineMessages, useIntl} from 'react-intl'
import OrderCancellationDetails from './order-cancellation-details'
import OrderInfo from './order-info'
import {useParams} from 'react-router-dom'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {MRT_API_BASEPATH} from '../../../../ssr/constants'
import CancellationConfirmationModal from './cancellation-confrimation-modal'
import {OrderContext} from '../order-context'
import OrderReturnHeader from './order-return-header'
import LoadingSpinner from '../../../../components/loading-spinner'
import {useTokenHelper} from '../../../../utils/token-helper'

const messages = defineMessages({
    cancellationErrorDefault: {
        id: 'order_summary_section.cancellation_error.default',
        defaultMessage: 'Unable to cancel order. Please try again shortly.',
    },
    cancellationErrorNetwork: {
        id: 'order_summary_section.cancellation_error.network',
        defaultMessage: 'Cancellation failed due to a network or server error.',
    },
})

const OrderSummarySection = React.memo(({order, refetchOrder}) => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [showCancelForm, setShowCancelForm] = useState(false)
    const {activeReturnShipmentId} = useContext(OrderContext)
    const [isLoading, setIsLoading] = useState(false)
    const [cancelError, setCancelError] = useState(null)
    const appOrigin = useOrigin(false)
    const {fetchOMSValidToken} = useTokenHelper()
    const {formatMessage} = useIntl()

    const params = useParams()

    const {orderId} = params

    const handleSubmitCancellation = useCallback(
        async (cancellationReason, additionalCancellationReason) => {
            try {
                setIsLoading(true)
                const accessToken = await fetchOMSValidToken()

                const cancelOrderPayload = {
                    orderSummaryId: orderId, // for server-side validation
                    allOrNone: true,
                    compositeRequest: [
                        {
                            method: 'PATCH',
                            url: `/services/data/v59.0/sobjects/OrderSummary/${orderId}`,
                            referenceId: 'refOrder',
                            body: {
                                IsCancelationFromCommerce__c: true,
                                CancelationReason__c: cancellationReason,
                                AdditionalCancelationReason__c: additionalCancellationReason,
                                CancelationDate__c: new Date(),
                            },
                        },
                    ],
                }

                const response = await fetch(`${appOrigin}${MRT_API_BASEPATH}/cancel-order-oms`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(cancelOrderPayload),
                })

                const result = await response.json()

                if (result.success) {
                    setCancelError(null)
                    onOpen()
                    setShowCancelForm(false)
                } else if (
                    result.error.includes('Cannot cancel the order after grace period has expired')
                ) {
                    setCancelError(
                        formatMessage({
                            defaultMessage:
                                'The order cannot be canceled after the grace period has expired.',
                            id: 'order_summary_section.cancellation_error.grace_period_expired',
                        }),
                    )
                } else {
                    setCancelError(result.error || formatMessage(messages.cancellationErrorDefault))
                }
            } catch (err) {
                console.error('Cancellation failed: ', err)
                setCancelError(formatMessage(messages.cancellationErrorNetwork))
            } finally {
                setIsLoading(false)
            }
        },
        [orderId, refetchOrder],
    )
    return (
        <Box position="relative">
            {isLoading && <LoadingSpinner />}
            {cancelError && (
                <Box mt={4} color="red.500" fontSize="sm" data-testid="cancellation-error">
                    {cancelError}
                </Box>
            )}
            {activeReturnShipmentId ? (
                <OrderReturnHeader
                    orderNumber={order?.orderNumber}
                    orderDate={order?.orderedDate}
                />
            ) : (
                <>
                    {showCancelForm ? (
                        <OrderCancellationDetails
                            order={order}
                            onSubmit={handleSubmitCancellation}
                        />
                    ) : (
                        <OrderInfo
                            order={order}
                            onCancelRequest={() => {
                                setCancelError(null)
                                setShowCancelForm(true)
                            }}
                        />
                    )}
                </>
            )}

            <CancellationConfirmationModal
                isOpen={isOpen}
                onClose={() => {
                    refetchOrder()
                    setCancelError(null)
                    onClose()
                }}
            />
        </Box>
    )
})

OrderSummarySection.propTypes = {
    order: PropTypes.object,
    refetchOrder: PropTypes.func,
}

OrderSummarySection.displayName = 'order-summary-section'

export default OrderSummarySection
